import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { GiftCardStepService } from '../../../giftcard/services/giftcard-step.service';
import { PaymentStateService } from '../../services/payment-state.service';
import { OrderStateService } from '../../../order/services/order-state.service';
import { LoggerService } from '../../../../services/logger/logger.service';
import { Order } from '../../../order/models/order';
import { OrderTypeEnum } from '../../../order/models/order-type.enum';
import { GiftCardStepEnum } from '../../../giftcard/models/giftcard-step.enum';
import { BookingStepEnum } from '../../../booking/models/booking-step.enum';
import { BookingStepService } from '../../../booking/services/booking-step.service';
import { LoadingSpinnerService } from '../../../../services/loading-spinner.service';
import { BookingOrderService } from '../../../booking/services/booking-order.service';
import { ShopSettingsService } from '../../../general/services/shop-settings.service';
import { environment } from '../../../../../environments/environment';
import { Observable, of, Subscription } from 'rxjs';
import { InactiveTimerService } from '../../../../services/inactive-timer.service';
import { NotificationService } from '../../../general/services/notification.service';
import { PartnerStateService } from '../../../partner/services/partner-state.service';
import { MatDialog } from '@angular/material/dialog';
import { PartnerEditPopupComponent } from '../../../partner/components/partner-edit-popup/partner-edit-popup.component';
import { LoginStateService } from '../../../login/services/login-state.service';
import { switchMap, tap } from 'rxjs/operators';
import { BookingProductStepService } from 'src/app/modules/booking/services/booking-product-step.service';
import { BookingProductStepEnum } from 'src/app/modules/booking/models/booking-product-step.enum';
import { ProductStockError } from 'src/app/modules/order/models/product-stock-error';
import { NoProductDialogComponent } from 'src/app/modules/order/components/no-product-dialog/no-product-dialog.component';
declare let fbq: Function;

@Component({
  selector: 'app-payment-start',
  templateUrl: './payment-start.component.html',
  styleUrls: ['./payment-start.component.css']
})
export class PaymentStartComponent implements OnInit, OnDestroy {

  private _createOrderAndStartPaymentSubscription: Subscription;
  private logoutSubscription: Subscription;
  updatePartnerSubscription: Subscription;

  private _initialized: boolean;

  constructor(
    private router: Router,
    private giftCardStepService: GiftCardStepService,
    private paymentStateService: PaymentStateService,
    private bookingStepService: BookingStepService,
    private orderStateService: OrderStateService,
    private loadingSpinnerService: LoadingSpinnerService,
    private logger: LoggerService,
    private bookingOrderService: BookingOrderService,
    private shopSettingsService: ShopSettingsService,
    private inactiveTimerService: InactiveTimerService,
    private notificationService: NotificationService,
    private partnerStateService: PartnerStateService,
    private loginStateService: LoginStateService,
    private bookingProductStepService: BookingProductStepService,
    private dialog: MatDialog,
  ) { }

  ngOnInit(): void {
    this.paymentStateService.reset();
    const sessionOrderId = this.orderStateService.sessionOrderId;
    if (!this.orderStateService.order && sessionOrderId) {
      this.loadingSpinnerService.show();
      this.orderStateService.loadOrder(sessionOrderId, this.orderStateService.guestOrderKey).subscribe(
        result => {
          this.loadingSpinnerService.hide();
          if (!result) {
            this.router.navigate(['partner/orders']);
          }
          // this.orderStateService.sessionOrderId = 0;
          this.logger.info(`PaymentStartComponent: loadOrder complete.`);
          if (this._order.orderType === OrderTypeEnum.Giftcard) {
            this.giftCardStepService.updateOrderLineProducts();
            this.giftCardStepService.setStep(GiftCardStepEnum.Payment);
          }

          this._initialized = true;
        },
        error => {
          this.loadingSpinnerService.hide();
          this.logger.error(`PaymentStartComponent: loadOrder error: ${error.message}.`);
          this._initialized = true;
        }
      );
    } else {
      this._initialized = true;
    }

    this.inactiveTimerService.resetTimer();
    this.inactiveTimerService.registerStopTimerAction(PaymentStartComponent.name, () => this.inactiveTimerEnd());
  }

  ngOnDestroy(): void {
    this.unsubscribeLogoutSubscription();
    this._unsubscribeCreateOrderAndStartPaymentSubscription();
    this.notificationService.hide(PaymentStartComponent.name);
    this.inactiveTimerService.unregisterStopTimerAction(PaymentStartComponent.name);
    this.unsubscribeUpdatePartnerSubscription();
    this._initialized = false;
  }

  get initialized(): boolean {
    return this._initialized;
  }

  get isAbortButton(): boolean {
    return this.shopSettingsService.isVUMode && (!this._order || !this._order.isNew);
  }

  back(): void {
    if (this._order) {
      this.paymentStateService.deleteExtraPayLine();
      const orderType = this._order.orderType;
      switch (orderType) {
        case OrderTypeEnum.Giftcard:
          if (this.giftCardStepService.step === GiftCardStepEnum.Payment) {
            this._logoutIfNeedBeforeCallMethod(() => { this.giftCardStepService.back(); });
            return;
          }
          break;
        case OrderTypeEnum.Product:
          if (this.bookingProductStepService.step === BookingProductStepEnum.Payment) {
            this._logoutIfNeedBeforeCallMethod(() => { this.bookingProductStepService.back(); });
            return;
          }
          break;
        default:
          if (this.bookingStepService.step !== BookingStepEnum.Payment || !this._order.isNew) {
            this.bookingStepService.initialize()
              .pipe(
                tap(_ => { this.orderStateService.orderSaveToLocalStorage(); }),
                switchMap(_ => this.bookingStepService.initFromLoadOrder(BookingStepEnum.Payment))
              )
              .subscribe(_ => {
                this._logoutIfNeedBeforeCallMethod(() => { this.bookingStepService.back(); });
              });
            return;
          }
          if (this.bookingStepService.step === BookingStepEnum.Payment) {
            this._logoutIfNeedBeforeCallMethod(() => { this.bookingStepService.back(); });
            return;
          }
      }
    }

    if (this.shopSettingsService.isVUMode) {
      this.cancelOrder();
      return;
    }
    this.router.navigate(['partner/orders']);
  }


  private _logoutIfNeedBeforeCallMethod(method: () => void): void {
    if (!method) {
      return;
    }
    if (this.isVUPartnerCodeAuthorization) {
      this.unsubscribeLogoutSubscription();
      this.logoutSubscription = this.loginStateService.logout().subscribe(
        _ => {
          method();
        });
    } else {
      method();
    }
  }

  unsubscribeLogoutSubscription(): void {
    if (this.logoutSubscription) {
      this.logoutSubscription.unsubscribe();
      this.logoutSubscription = null;
    }
  }

  get isVUPartnerCodeAuthorization(): boolean {
    return this.shopSettingsService.isVUMode && this.shopSettingsService.shopSettings.partnerCodeAuthorization;
  }

  pay(): void {
    let showPartnerEditForm = false;
    const partner = this.partnerStateService.loggedInPartner;
    if (partner && !this.shopSettingsService.isVUMode) {
      const showFullName = this.shopSettingsService.showFullNameInRegistration;
      if (showFullName && (!partner.firstName || !partner.lastName)) {
        showPartnerEditForm = true;
      }

      const showAddress = this.shopSettingsService.showAddressInRegistration;
      if (showAddress && (!partner.street || !partner.zip || !partner.city)) {
        showPartnerEditForm = true;
      }
    }

    if (showPartnerEditForm) {
      this.dialog.open(PartnerEditPopupComponent, {
        data: {
          'partner': partner,
          'title': 'Please check your invoice information:',
          'eventConfirmButton': () => {
            this._updatePartnerAndPay(partner);
          },
        }
      });
    } else {
      this._pay();
    }
  }

  private _updatePartnerAndPay(partner): void {
    if (!partner) {
      return;
    }

    this.unsubscribeUpdatePartnerSubscription();
    this.updatePartnerSubscription = this.partnerStateService.updatePartner(partner).subscribe(
      done => {
        this._pay();
      },
      error => {
        this.logger.error(`PaymentStartComponent: _updatePartnerAndPay error: ${error.message || error}.`);
      }
    );
  }

  _pay(): void {
    if (!this._order) {
      this.logger.error('No order for payment');
    }

    if (!this.paymentEnabled) {
      this.logger.error('Payment in progress');
      return;
    }

    this.paymentEnabled = false;
    this._trackFacebookAnalytics();
    this.loadingSpinnerService.show();
    this.notificationService.hide(PaymentStartComponent.name);
    this._unsubscribeCreateOrderAndStartPaymentSubscription();
    this.pauseInactiveTimer(true);
    this._createOrderAndStartPaymentSubscription = this._createOrderAndStartPayment().subscribe(
      (paymentComplete) => {
        this.pauseInactiveTimer(false);
        this.logger.info(`Payment complete. result: ${paymentComplete}`);
        if (paymentComplete) {
          if (this.shopSettingsService.isVUMode) {
            this._logoutIfNeedBeforeCallMethod(() => {
              this.logger.info(`Payment complete. redirect to: /payment/vu-complete`);
              this.router.navigateByUrl('payment/vu-complete');
              this.paymentEnabled = true;
              this.loadingSpinnerService.hide();
            });
            return;
          } else {
            if (this.orderStateService.guestOrderKey) {
              this.logger.info(`Payment complete. redirect to: /payment/guest/return`);
              this.router.navigateByUrl(`/payment/guest/return/${this.orderStateService.guestOrderKey}/${this._order.id}`);
            } else {
              this.logger.info(`Payment complete. redirect to: /payment/return`);
              this.router.navigateByUrl('payment/return/' + this._order.id);
            }
          }
        }
        this.paymentEnabled = true;
        this.loadingSpinnerService.hide();
      },
      error => {
        const errorMessage = error && error.message ? error.message : error;
        this.logger.error(errorMessage);
        if (error instanceof ProductStockError) {
          this.showNoProductStockDialog(error);
        } else {
          this.notificationService.show(PaymentStartComponent.name, errorMessage);
        }
        this.paymentEnabled = this.bookingOrderService.order?.totalQuantity > 0;
        this.pauseInactiveTimer(false);
        this.loadingSpinnerService.hide();
      },
      () => {
        this.paymentEnabled = true;
        this.pauseInactiveTimer(false);
        this.loadingSpinnerService.hide();
      }
    );
  }

  showNoProductStockDialog(productStockError: ProductStockError): void {
    const dialogRef = this.dialog.open(NoProductDialogComponent, {
      data: {
        productStockError,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.back();
      }
    });
  }

  private pauseInactiveTimer(pause: boolean): void {
    if (pause) {
      this.inactiveTimerService.clearTimer();
    } else {
      this.inactiveTimerService.initInactiveTimer();
    }
  }

  private _unsubscribeCreateOrderAndStartPaymentSubscription(): void {
    if (this._createOrderAndStartPaymentSubscription) {
      if (!this._createOrderAndStartPaymentSubscription.closed) {
        this.loadingSpinnerService.hide();
      }
      this._createOrderAndStartPaymentSubscription.unsubscribe();
      this._createOrderAndStartPaymentSubscription = null;
    }
  }

  private _trackFacebookAnalytics(): void {
    if (this.shopSettingsService.facebookAnalyticsId) {
      const content = [];
      const location = environment.odooDatabaseName;
      for (const orderLine of this._order.orderLines) {
        content.push({ id: orderLine.productId, quantity: orderLine.quantity, item_price: orderLine.price });
      }

      const areaAreaItemPackName = this.bookingOrderService.areaAreaItemPack
        && this.bookingOrderService.areaAreaItemPack.name ? this.bookingOrderService.areaAreaItemPack.name : this._order.orderType;
      fbq('track', 'InitiateCheckout', {
        //        'contents': content,
        value: this._order.totalAmount,
        currency: environment.globalSettings.isoCurrencyCode,
        location,
        content_name: areaAreaItemPackName,
      });
    }
  }

  private get _order(): Order {
    return this.orderStateService.order;
  }

  private _createOrderAndStartPayment(): Observable<boolean> {
    return of(false).pipe(
      switchMap(_ => {
        this.logger.info(`PaymentStartComponent: Start Payment`);
        if (this._order.orderType === OrderTypeEnum.Giftcard) {
          return this.giftCardStepService.createOrder();
        }
        if (this._order.orderType === OrderTypeEnum.Product) {
          return this.orderStateService.createOrder();
        }
        if (this.bookingStepService.isBookingOrderAvailable) {
          return this.bookingOrderService.createOrder();
        }
        return this.orderStateService.createOrder();
      }),
      switchMap(_ => this.paymentStateService.startPayment()),
    );
  }

  private cancelOrder(): void {
    if (this._order && !this._order.isNew) {
      this.loadingSpinnerService.show();
      this.orderStateService.sessionOrderId = 0;
      this.bookingOrderService.cancelOrder(this._order.id).subscribe(
        result => {
          this.redirectToShopMainPage();
          this.loadingSpinnerService.hide();
        },
        error => {
          this.logger.error(error);
          this.redirectToShopMainPage();
          this.loadingSpinnerService.hide();
        }
      );
    } else {
      this.redirectToShopMainPage();
    }
  }

  private redirectToShopMainPage(): void {
    this.router.navigate(['/']);
  }

  private inactiveTimerEnd(): Observable<any> {
    if (this._order && !this._order.isNew) {
      return this.bookingOrderService.cancelOrder(this._order.id);
    } else {
      return of(null);
    }
  }

  private unsubscribeUpdatePartnerSubscription() {
    if (this.updatePartnerSubscription) {
      this.updatePartnerSubscription.unsubscribe();
      this.updatePartnerSubscription = null;
    }
  }

  get paymentEnabled(): boolean {
    return this.paymentStateService.paymentEnabled;
  }

  set paymentEnabled(value: boolean) {
    if (value === this.paymentStateService.paymentEnabled) {
      return;
    }

    this.paymentStateService.paymentEnabled = value;
  }
}
